import { Alert, Box, Stack, TextField } from '@mui/material';
import QuestionField from '../components/QuestionField';
import ChoiceGroup from '../components/ChoiceGroup';
import SelectInput from '../components/SelectInput';
import ConditionalBranch from '../components/ConditionalBranch';
import RepeatableTextList from '../components/RepeatableTextList';
import FileDropList from '../components/FileDropList';
import StepFooter from '../components/StepFooter';
import { checkDataFileColumns, requiredColumns } from '../dataFileColumns';
import { FILE_KIND, OUTCOME_DATA_SOURCE, REFERENCE_GROUP } from '../../registryChoices';

// Data file explanation (Analyst Institute wording, per PAD-4643). Static: the text names
// every vendor's ID column and says only Catalist needs a state column.
const DATA_FILE_HELP = [
  'If you are using individual-level data, this should include a unique id in your first column (e.g., if you',
  'are using Catalist, include the dwid in your first column, and if you are using TargetSmart the voterbase_id).',
  "If you are using Catalist data, you should include a state abbreviation in your second column (e.g., 'WI') -",
  "this is not required for other sources. Name the first column '<id name>' (e.g., dwid) and the second column",
  "'state' (if using Catalist). Files should also include a subsequent 'condition' column identifying the",
  'experimental conditions each ID has been assigned to: ensure that your condition column is set up with a 0',
  'for control (if relevant), 1 for the first treatment you enter in this form, 2 for the second treatment you',
  'enter in this form, and so on. Using other values for conditions will result in matching errors. If you are',
  'using household identifiers in addition to individual-level IDs, then please include those as well in a',
  "subsequent column. Please do not include any additional PII. Format: a .csv with the first column '<id name>'",
  "with your voter ID (dwid for Catalist, voterbase_id for TargetSmart), second column 'state' with the state",
  "abbreviation for that ID (e.g., 'WI') if using Catalist data, and next column 'condition' with the condition",
  'number (e.g., 0, 1, 2, etc.). Household IDs or other columns can follow. If you are uploading a file with a',
  'non-Catalist or TargetSmart ID, follow the same format.',
].join(' ');

const REQUIRED = 'This question is required.';
const NEEDS_TREATMENT = 'Experiments are required to have at least one treatment. Add a treatment to proceed.';
const YES_NO = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];
const NUM_TREATMENT_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n) }));

const Step3Experiment = ({
  formData,
  setField,
  showErrors,
  onNext,
  onBack,
  experimentId,
  files,
  setFiles,
  saving,
  saveOutcomeSource,
}) => {
  const err = (missing) => (showErrors && missing ? REQUIRED : '');
  const docFiles = files.filter((f) => f.kind === FILE_KIND.ADDITIONAL_DOC);
  const untaggedDocs = docFiles.some((f) => !f.document_category);
  const dataFiles = files.filter((f) => f.kind === FILE_KIND.DATA);
  const replaceKind = (kind, updated) => setFiles([...files.filter((f) => f.kind !== kind), ...updated]);

  return (
    <>
      <Stack spacing={3.25}>
        <QuestionField
          label="Please provide a brief description of your program and how it is being evaluated."
          required
          helper="Include the purpose of your experiment and overview of your treatments (including e.g., when and where it's taking place, what is the mode and content of the treatment, etc.). This should give readers a comprehensive summary of your experiment."
          error={err(!formData.experiment_description.trim())}
        >
          <TextField
            hiddenLabel
            fullWidth
            multiline
            minRows={3}
            value={formData.experiment_description}
            onChange={(e) => setField('experiment_description', e.target.value)}
            error={Boolean(err(!formData.experiment_description.trim()))}
          />
        </QuestionField>

        <QuestionField label="What are the research questions this project is trying to answer?">
          <RepeatableTextList
            value={formData.research_questions}
            onChange={(v) => setField('research_questions', v)}
          />
        </QuestionField>

        <QuestionField
          label="Please describe how you are targeting your experiment."
          helper="(i.e., what is your criteria for selecting individuals, households, precincts, etc. to include in your experiment)"
        >
          <TextField
            hiddenLabel
            fullWidth
            multiline
            minRows={3}
            value={formData.targeting_criteria}
            onChange={(e) => setField('targeting_criteria', e.target.value)}
          />
        </QuestionField>

        <QuestionField
          label="Please describe how units are being allocated to each treatment condition."
          required
          helper="This should include whether randomization is simple, blocked, stratified, or adaptive. This also should include naming the statistical software and package used, as well as a seed."
          error={err(!formData.randomization_description.trim())}
        >
          <TextField
            hiddenLabel
            fullWidth
            multiline
            minRows={3}
            value={formData.randomization_description}
            onChange={(e) => setField('randomization_description', e.target.value)}
            error={Boolean(err(!formData.randomization_description.trim()))}
          />
        </QuestionField>

        <QuestionField label="Is your randomization clustered?" required error={err(formData.clustered === null)}>
          <ChoiceGroup
            layout="inline"
            options={YES_NO}
            value={formData.clustered}
            onChange={(v) => setField('clustered', v)}
          />
        </QuestionField>
        {formData.clustered === true && (
          <ConditionalBranch>
            <QuestionField
              label="What variable(s) are you clustering on?"
              required
              error={err(!formData.cluster_level.trim())}
            >
              <TextField
                hiddenLabel
                fullWidth
                size="small"
                value={formData.cluster_level}
                onChange={(e) => setField('cluster_level', e.target.value)}
                error={Boolean(err(!formData.cluster_level.trim()))}
              />
            </QuestionField>
          </ConditionalBranch>
        )}

        <QuestionField
          label="Please describe any rules you will be using to stop delivering any of your treatments."
          helper="(e.g., are there any safety, cost, or other types of concerns that would make you stop implementing any of your treatments?)"
        >
          <TextField
            hiddenLabel
            fullWidth
            multiline
            minRows={3}
            value={formData.stopping_rules}
            onChange={(e) => setField('stopping_rules', e.target.value)}
          />
        </QuestionField>

        <QuestionField
          label="How many treatments will you be testing in this experiment?"
          required
          helper="Do not include the control condition in this count. If you are running an a/b test or a placebo test without a pure control group that is not contacted, count all of the conditions."
          error={showErrors && !formData.num_treatments ? NEEDS_TREATMENT : ''}
        >
          <SelectInput
            options={NUM_TREATMENT_OPTIONS}
            value={formData.num_treatments}
            onChange={(v) => setField('num_treatments', v)}
            width={220}
          />
        </QuestionField>

        <QuestionField
          label="What will the reference group be in your experiment?"
          required
          error={err(formData.reference_group.length === 0)}
        >
          <ChoiceGroup
            multiple
            options={REFERENCE_GROUP}
            value={formData.reference_group}
            onChange={(v) => setField('reference_group', v)}
          />
        </QuestionField>

        <QuestionField
          label="What will be the source of the outcome data of your experiment?"
          required
          error={err(!formData.outcome_data_source)}
        >
          <SelectInput
            options={OUTCOME_DATA_SOURCE}
            value={formData.outcome_data_source}
            onChange={(v) => setField('outcome_data_source', v)}
            error={Boolean(err(!formData.outcome_data_source))}
          />
        </QuestionField>
        {formData.outcome_data_source === 'other' && (
          <ConditionalBranch>
            <QuestionField
              label="Please write in the source of the outcome data"
              required
              error={err(!formData.outcome_data_source_other.trim())}
            >
              <TextField
                hiddenLabel
                fullWidth
                size="small"
                value={formData.outcome_data_source_other}
                onChange={(e) => setField('outcome_data_source_other', e.target.value)}
              />
            </QuestionField>
          </ConditionalBranch>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
          <QuestionField label="What date will your program start?">
            <TextField
              type="date"
              hiddenLabel
              size="small"
              value={formData.program_start_timing || ''}
              onChange={(e) => setField('program_start_timing', e.target.value)}
              sx={{ width: 220 }}
            />
          </QuestionField>
          <QuestionField label="What date will your program end?">
            <TextField
              type="date"
              hiddenLabel
              size="small"
              value={formData.program_end_timing || ''}
              onChange={(e) => setField('program_end_timing', e.target.value)}
              sx={{ width: 220 }}
            />
          </QuestionField>
        </Box>

        <QuestionField
          label="Please upload any additional documents related to your experiment."
          helper="This might include an experimental protocol, randomization plan, pre-analysis plan, implementation plan, data dictionary, randomization code, or any other document that you would like to share. Select a document type for each file you upload."
          error={showErrors && untaggedDocs ? 'Select a document type for each uploaded document.' : ''}
        >
          <FileDropList
            experimentId={experimentId}
            kind={FILE_KIND.ADDITIONAL_DOC}
            files={docFiles}
            onChange={(updated) => replaceKind(FILE_KIND.ADDITIONAL_DOC, updated)}
            showCategory
            requireCategory={showErrors}
          />
        </QuestionField>

        <QuestionField
          label="Please upload the unique identifier data and the associated treatment assignments for your experiment."
          required
          helper={DATA_FILE_HELP}
          error={err(dataFiles.length === 0)}
        >
          <Alert severity="info" sx={{ mb: 1 }}>
            {formData.outcome_data_source === 'other' ? (
              <>
                Required columns: <strong>your ID</strong> · <strong>condition</strong>
              </>
            ) : formData.outcome_data_source ? (
              <>
                Required columns: <strong>{requiredColumns(formData.outcome_data_source).join(' · ')}</strong>
              </>
            ) : (
              <>
                Required columns: <strong>dwid · state · condition</strong> for Catalist, or{' '}
                <strong>voterbase_id · condition</strong> for TargetSmart
              </>
            )}{' '}
            (condition: 0 = control, 1 = first treatment, 2 = second treatment, and so on).
          </Alert>
          <FileDropList
            experimentId={experimentId}
            kind={FILE_KIND.DATA}
            files={dataFiles}
            onChange={(updated) => replaceKind(FILE_KIND.DATA, updated)}
            multiple={false}
            accept=".csv"
            // The server checks the file's ID column against the chosen outcome source, so
            // that answer has to be saved before the upload, not just when leaving the page.
            beforeUpload={saveOutcomeSource}
            // Catch a missing required column before uploading, not after.
            validateFile={(file) => checkDataFileColumns(file, formData.outcome_data_source)}
            disabledReason={
              formData.outcome_data_source ? '' : 'Choose the outcome data source above before uploading.'
            }
            error={Boolean(err(dataFiles.length === 0))}
          />
        </QuestionField>

        <QuestionField label="What is the total number of individual units in your experiment (N)?">
          <TextField
            type="number"
            hiddenLabel
            size="small"
            value={formData.total_n ?? ''}
            onChange={(e) => setField('total_n', e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ width: 220 }}
          />
        </QuestionField>
      </Stack>

      <StepFooter onBack={onBack} onNext={onNext} nextDisabled={saving} />
    </>
  );
};

export default Step3Experiment;
